import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Base } from '@itsrever/design-system'
import { ShippingConfiguration } from '@/hooks'
import { ShippingRule } from '@/application/manage-shipping'
import { EU_ALIAS_NAME } from '@/domain/countries'
import { useStoreCurrency } from '@/hooks/use-store-currency'
import { formatCurrency } from '@/utils'
import { OptionsPicker } from '@/ui/components/rule-list'
import { Cost } from './cost-cell'
import { TestReturn } from './shipping-rules'

const {
  Card,
  IconButton,
  Input,
  FieldRoot,
  FieldLabel,
  ToggleGroup,
  ToggleGroupItem
} = Base

export const newTestReturn = (
  rules: ShippingRule[],
  config: ShippingConfiguration
): TestReturn => ({
  country: testCountries(rules, config).shown[0] ?? '',
  refundMethod: preferred(
    valuesOf('refundMethod', config),
    'Original payment method'
  ),
  returnMethod: preferred(valuesOf('returnMethod', config), 'Home pick-up'),
  orderTotalAmount: '80'
})

// The most common return, as useAvailableShippingRules labels it.
const preferred = (values: string[], value: string) =>
  values.includes(value) ? value : (values[0] ?? '')

const valuesOf = (fn: string, config: ShippingConfiguration) =>
  config.functions.find((f) => f.function === fn)?.values ?? []

// The countries the rules name, then a few common ones; the rest under "Other".
export function testCountries(
  rules: ShippingRule[],
  config: ShippingConfiguration
) {
  const all = valuesOf('country', config).filter((c) => c !== EU_ALIAS_NAME)
  const named = rules.flatMap((rule) =>
    rule.conditions.flatMap((c) => (c.function === 'country' ? c.values : []))
  )
  const common = [
    'Spain',
    'France',
    'Germany',
    'United Kingdom',
    'United States'
  ]
  const shown = [...new Set([...named, ...common])]
    .filter((c) => all.includes(c))
    .slice(0, 6)
  return { shown, others: all.filter((c) => !shown.includes(c)) }
}

export const TestPanel: React.FC<{
  test: TestReturn
  rules: ShippingRule[]
  availableConfiguration: ShippingConfiguration
  result: Cost
  resultRule: number
  skipped: number[]
  onChange: (test: TestReturn) => void
  onClose: () => void
}> = ({
  test,
  rules,
  availableConfiguration,
  result,
  resultRule,
  skipped,
  onChange,
  onClose
}) => {
  const { t } = useTranslation()
  const currency = useStoreCurrency()
  const { shown, others } = testCountries(rules, availableConfiguration)
  const free = Number(result.cost) === 0

  return (
    <Card
      variant="muted"
      className="relative flex flex-wrap items-stretch gap-6 p-5 pr-12"
    >
      <IconButton
        aria-label={t('shipping_rules.close_test')}
        className="absolute right-2 top-2"
        onClick={onClose}
      >
        ×
      </IconButton>
      <div className="flex min-w-0 flex-1 flex-wrap content-start gap-8">
        <Chips
          label={t('edit_shipping.function_names.country')}
          options={shown}
          others={others}
          value={test.country}
          onChange={(country) => onChange({ ...test, country })}
        />
        <Chips
          label={t('edit_shipping.function_names.refundMethod')}
          options={valuesOf('refundMethod', availableConfiguration)}
          value={test.refundMethod}
          onChange={(refundMethod) => onChange({ ...test, refundMethod })}
        />
        <Chips
          label={t('edit_shipping.function_names.returnMethod')}
          options={valuesOf('returnMethod', availableConfiguration)}
          value={test.returnMethod}
          onChange={(returnMethod) => onChange({ ...test, returnMethod })}
        />
        <FieldRoot className="w-32">
          <FieldLabel>
            {t('edit_shipping.function_names.orderTotalAmount')}
          </FieldLabel>
          <Input
            size="sm"
            inputMode="decimal"
            value={test.orderTotalAmount}
            onChange={(event) =>
              onChange({
                ...test,
                orderTotalAmount: event.target.value.replace(',', '.')
              })
            }
          />
        </FieldRoot>
      </div>
      <Card
        role="status"
        aria-label={t('shipping_rules.test_result')}
        className="flex h-auto w-60 shrink-0 flex-col gap-1 self-start p-4"
      >
        <p className="text-xs font-medium uppercase tracking-wide text-neutral-400">
          {t('shipping_rules.test_result')}
        </p>
        <p className="text-xs text-neutral-500">
          {t('shipping_rules.test_customer_pays')}
        </p>
        <p
          className={
            'text-xl font-medium tabular-nums ' +
            (free ? 'text-success-main' : 'text-neutral-900')
          }
        >
          {free
            ? t('shipping_rules.free')
            : formatCurrency(result.cost, currency)}
          {result.returnOriginalShippingCosts ? (
            <span className="ml-1 text-xs font-normal text-neutral-400">
              {t('shipping_rules.plus_original')}
            </span>
          ) : null}
        </p>
        <p className="text-xs text-neutral-500">
          {resultRule < 0 ? (
            t('shipping_rules.test_no_match')
          ) : (
            <Trans
              i18nKey="shipping_rules.test_set_by"
              values={{
                rule: t('shipping_rules.rule_label', { n: resultRule + 1 })
              }}
              components={{
                b: <span className="font-medium text-primary-base" />
              }}
            />
          )}
        </p>
        {skipped.length ? (
          <p className="text-xs text-neutral-400">
            {t('shipping_rules.test_skipped', {
              count: skipped.length,
              rules: skipped.map((i) => i + 1).join(', ')
            })}
          </p>
        ) : null}
      </Card>
    </Card>
  )
}

const Chips: React.FC<{
  label: string
  options: string[]
  others?: string[]
  value: string
  onChange: (value: string) => void
}> = ({ label, options, others = [], value, onChange }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const pickedOther = others.includes(value)

  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-xs text-neutral-500">{label}</p>
      <div className="flex flex-wrap gap-2">
        <ToggleGroup
          variant="button"
          aria-label={label}
          value={[value]}
          // A test return always has a value: pressing the picked option again keeps it.
          onValueChange={([next]: string[]) => next && onChange(next)}
        >
          {options.map((option) => (
            <ToggleGroupItem key={option} value={option}>
              {option}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        {others.length ? (
          <OptionsPicker
            options={others.map((o) => ({ value: o, label: o }))}
            value={pickedOther ? [value] : []}
            onChange={([picked]) => picked && onChange(picked)}
            open={open}
            onOpenChange={setOpen}
            triggerLabel={t('shipping_rules.other_value', { field: label })}
            trigger={<ToggleGroupItem pressed={pickedOther} />}
            triggerContent={pickedOther ? value : t('shipping_rules.other')}
          />
        ) : null}
      </div>
    </div>
  )
}
